import React, { useState, useMemo } from 'react';
import { useFlags } from 'helpers/useFlags';
import { useSelector } from 'react-redux';

import { PaperSlidingLayout } from 'ui/components/Paper/PaperSlidingLayout';
import { PageWithAdvancedSearch } from 'ui/components/Page/PageWithAdvancedSearch';
import { ConfirmationModal } from 'ui/components/Modal/ConfirmationModal';
import { SHIPPING_TERMS } from 'services/items';
import { CustomFieldsModal } from 'ui/components/CustomFields/CustomFieldsModal';
import { ReportId } from 'services/reports';
import { ReportsModal } from 'ui/components/Modal/ReportsModal';
import { ObjectType } from 'services/customFields';
import {
  SalesOrderSearchResults,
  SalesOrderDetailsCard,
  SalesOrderAdvancedSearch,
  SALES_ORDER_COLUMNS,
} from './components';
import { SalesOrderPageViewProps } from './types';
import {
  SalesOrderTableRowActions,
  SalesOrderPageAction,
} from './components/SalesOrderSearchResults/types';
import {
  initialSalesOrderFormValues,
  advancedSearchReduxActions,
  createDisplayValueMap,
  initialPagination,
  displayNameMap,
} from './consts';
import { FallbackUI } from 'ui/components/Modal/ErrorMessage/common';
import { ErrorBoundary } from 'react-error-boundary';
import { ModuleNavigation } from 'app/components/AppBar/components';
import BulkQuickFulfillModal from './components/BulkQuickFulfillModal';
import { shouldShowQuickFulfill } from './components/SalesOrderDetailsCard/helpers';
import { disableQuickFulfillHandler } from './components/SalesOrderTitleBar/SalesOrderTitleBar';

import {
  SalesOrder,
  SalesOrderStatus,
  SalesOrderItemTypes,
  SalesOrderItemStatus,
  quickFulfillSalesOrder,
  fetchSalesOrderById,
} from 'services/salesOrders';
import { StockStatus } from 'ui/theme/components/FBOStockStatusTag/types';
import { getActiveUser } from 'services/user/redux/selectors';
import { useBulkQuickFulfill } from './hooks/useBulkQuickFulfill';

export default function SalesOrderPageView({
  data,
  uiState,
  lookups,
  selection,
  handlers,
}: SalesOrderPageViewProps) {
  const { searchResult, activeSalesOrderId, searchTags, intlFormatDate } = data;

  const {
    isLoading,
    isLoadingSearchResult,
    activeDate,
    fileImportInputRef,
    nextSalesOrdersNumber,
    clone,
  } = uiState;

  const {
    carriers,
    customers,
    locations,
    orderPriorities,
    paymentTerms,
    representatives,
  } = lookups;

  const { selectedItems, setSelectedItems } = selection;

  const {
    fetchSearchResult,
    handleAction,
    handleActiveSalesOrderClose,
    handleAddNewPress,
    handlePageAction,
    handlePaginationChange,
    handleSalesOrderClick,
    setClone,
    setNextSalesOrdersNumber,
    onFileSelectedForImport,
  } = handlers;

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isUnissueModalOpen, setIsUnissueModalOpen] = useState(false);
  const [isIssueModalOpen, setIsIssueModalOpen] = useState(false);
  const [isIssueProcessing, setIsIssueProcessing] = useState(false);
  const [isCustomFieldsModalOpen, setIsCustomFieldsModalOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);

  const determineEligibility = (
    order: SalesOrder
  ): { eligible: boolean; reason: string; code: string } => {
    if (!order || typeof order.id !== 'number' || order.id <= 0) {
      return {
        eligible: false,
        reason: 'Order data is invalid or missing ID.',
        code: 'INVALID_ORDER_DATA',
      };
    }

    // Check order status first. Only Issued or PartiallyFulfilled are potentially eligible.
    if (
      order.status !== SalesOrderStatus.Issued &&
      order.status !== SalesOrderStatus.PartiallyFulfilled
    ) {
      return {
        eligible: false,
        reason: `Order status (${order.status}) not eligible for quick fulfilment.`,
        code: 'INVALID_STATUS',
      };
    }

    // If status is Issued or PartiallyFulfilled, then check stock status.
    if (order.stockStatus === StockStatus.NoStock) {
      return {
        eligible: false,
        reason: 'No stock available.',
        code: 'NO_STOCK',
      };
    }
    if (order.stockStatus === StockStatus.PartialStock) {
      return {
        eligible: false,
        reason: 'Partial stock available.',
        code: 'PARTIAL_STOCK',
      };
    }
    if (order.stockStatus == null) {
      // For an Issued/PartiallyFulfilled order, if stockStatus is null or undefined, it means we couldn't determine it.
      return {
        eligible: false,
        reason: 'Insufficient inventory to fulfill.',
        code: 'MISSING_STOCK_STATUS',
      };
    }

    // Sales Items Check
    if (!order.salesItems || order.salesItems.length === 0) {
      return {
        eligible: false,
        reason: 'No sales items found.',
        code: 'NO_SALES_ITEMS',
      };
    }

    const hasNonDeletedItems = order.salesItems.some((item) => !item.deleted);
    if (!hasNonDeletedItems) {
      return {
        eligible: false,
        reason: 'All sales items are marked as deleted.',
        code: 'ALL_ITEMS_DELETED',
      };
    }

    if (!shouldShowQuickFulfill(order)) {
      const isAllCreditReturnOrDropShipItems = order.salesItems.every(
        (item) =>
          item.salesOrderItemType === SalesOrderItemTypes.CreditReturn ||
          item.salesOrderItemType === SalesOrderItemTypes.DropShip ||
          item.status === SalesOrderItemStatus.Fulfilled ||
          item.deleted
      );
      if (isAllCreditReturnOrDropShipItems) {
        return {
          eligible: false,
          reason:
            'All items are credit return, drop ship, fulfilled, or deleted.',
          code: 'ALL_ITEMS_INELIGIBLE_TYPE',
        };
      }
      return {
        eligible: false,
        reason:
          'Order not eligible based on item types or status (checked by shouldShowQuickFulfill).',
        code: 'INELIGIBLE_BY_SHOULD_SHOW_QUICK_FULFILL',
      };
    }

    if (disableQuickFulfillHandler(order)) {
      return {
        eligible: false,
        reason: 'Order contains unfulfillable tracked items.',
        code: 'UNFULFILLABLE_TRACKED_ITEMS',
      };
    }

    return { eligible: true, reason: '', code: 'ELIGIBLE' };
  };

  const stockStatusMap = new Map<number, StockStatus | null>();
  searchResult.items.forEach((order) => {
    if (order.id) {
      // Ensure id is not null/undefined before using as key
      stockStatusMap.set(order.id, order.stockStatus as StockStatus | null);
    }
  });

  const {
    isBulkQuickFulfillModalOpen,
    bulkQuickFulfillStage,
    bulkQuickFulfillEligibleOrders,
    bulkQuickFulfillIneligibleOrders,
    bulkQuickFulfillProgressDetails,
    isStoppingBulkQuickFulfill,
    openModalAndPrepareOrders,
    closeBulkQuickFulfillModal,
    confirmAndProcessBulkQuickFulfill,
    stopBulkQuickFulfillProcessing,
    estimatedTimePerOrderSeconds,
  } = useBulkQuickFulfill({
    selectedOrderIds: selectedItems,
    fetchSalesOrderById,
    quickFulfillSalesOrder,
    stockStatusMap,
    determineEligibility,
    setSelectedItems,
    fetchSearchResult,
  });

  const activeUser = useSelector(getActiveUser);
  const { enableSalesOrderBulkQuickFulfill } = useFlags();

  const openDeleteModal = () => setIsDeleteModalOpen(true);
  const closeDeleteModal = () => setIsDeleteModalOpen(false);

  const handleDeleteConfirmAndClose = async () => {
    await handlePageAction(SalesOrderPageAction.DeleteMultiple);
    closeDeleteModal();
  };

  const openUnissueModal = () => setIsUnissueModalOpen(true);
  const closeUnissueModal = () => setIsUnissueModalOpen(false);

  const handleUnissueConfirmAndClose = async () => {
    await handlePageAction(SalesOrderPageAction.Unissue);
    closeUnissueModal();
  };

  const openIssueModal = () => setIsIssueModalOpen(true);
  const closeIssueModalLocal = () => setIsIssueModalOpen(false);

  const handleIssueConfirmAndClose = async () => {
    setIsIssueProcessing(true);
    try {
      await handlePageAction(SalesOrderPageAction.Issue);
    } finally {
      setIsIssueProcessing(false);
      closeIssueModalLocal();
    }
  };

  const openCustomFieldsModal = () => setIsCustomFieldsModalOpen(true);

  const openReportModal = () => setIsReportModalOpen(true);
  const closeReportModal = () => setIsReportModalOpen(false);

  const handleActionInView = (type: SalesOrderTableRowActions, value: any) => {
    handleAction(type, value);
  };

  const handlePageActionInView = (type: SalesOrderPageAction, value?: any) => {
    if (type === SalesOrderPageAction.CustomFields) {
      openCustomFieldsModal();
      handlePageAction(type, value);
    } else if (type === SalesOrderPageAction.ShowReport) {
      handlePageAction(type, value);
      openReportModal();
    } else {
      handlePageAction(type, value);
    }
  };

  const displayValueMap = useMemo(
    () =>
      createDisplayValueMap(
        customers,
        representatives,
        carriers,
        orderPriorities,
        paymentTerms,
        SHIPPING_TERMS,
        locations,
        searchTags,
        intlFormatDate
      ),
    [
      customers,
      representatives,
      carriers,
      orderPriorities,
      paymentTerms,
      SHIPPING_TERMS,
      locations,
      searchTags,
      intlFormatDate,
    ]
  );

  return (
    <ErrorBoundary FallbackComponent={FallbackUI}>
      <ModuleNavigation />
      <PageWithAdvancedSearch
        detailCardColumns={SALES_ORDER_COLUMNS(activeDate)}
        initialFormValues={initialSalesOrderFormValues}
        advancedSearchReduxActions={advancedSearchReduxActions}
        searchResult={searchResult}
        fetchSearchResult={fetchSearchResult}
        AdvancedSearchFieldsCmp={SalesOrderAdvancedSearch}
        displayNameMap={displayNameMap}
        displayValueMap={displayValueMap}
        showAllLabel="Show All Orders"
        pageName="Sales Orders"
      >
        <PaperSlidingLayout shown={Boolean(activeSalesOrderId)}>
          <SalesOrderSearchResults
            salesOrders={searchResult.items}
            activeSalesOrderId={activeSalesOrderId}
            handleSalesOrderClick={handleSalesOrderClick}
            pagination={searchResult.pagination || initialPagination}
            onPaginationChange={handlePaginationChange}
            onAddNewPress={handleAddNewPress}
            isLoadingSalesOrders={isLoadingSearchResult}
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
            onAction={handleActionInView}
            onDeleteClicked={openDeleteModal}
            onPageAction={handlePageActionInView}
            activeSalesOrderDate={activeDate}
            onUnissueClicked={openUnissueModal}
            onIssueClicked={openIssueModal}
            onBulkQuickFulfillClicked={(_selectedOrderIds: number[]) => {
              if (enableSalesOrderBulkQuickFulfill) {
                openModalAndPrepareOrders();
              }
            }}
            showBulkQuickFulfillButton={enableSalesOrderBulkQuickFulfill}
            activeUser={activeUser}
          />
          <SalesOrderDetailsCard
            activeSalesOrderId={activeSalesOrderId}
            nextSalesOrdersNumber={nextSalesOrdersNumber}
            onClose={handleActiveSalesOrderClose}
            setNextSalesOrderNumber={setNextSalesOrdersNumber}
            fetchSearchResult={fetchSearchResult}
            clone={clone}
            setClone={setClone}
          />
        </PaperSlidingLayout>
      </PageWithAdvancedSearch>
      <ConfirmationModal
        open={isDeleteModalOpen}
        title="Delete Sales Order"
        body={`This will delete all selected sales orders and all related sales order items, are you sure?`}
        onCancelClicked={closeDeleteModal}
        onConfirmClicked={handleDeleteConfirmAndClose}
        confirmLabel={'Delete'}
        showCloseIcon
        confirmButtonRed
      />
      <ConfirmationModal
        open={isUnissueModalOpen}
        title="Unissue Sales Order"
        body={`This will unissue all selected sales orders and all related sales order items, are you sure?`}
        onCancelClicked={closeUnissueModal}
        onConfirmClicked={handleUnissueConfirmAndClose}
        confirmLabel={'Unissue'}
        confirmButtonRed
        isLoading={isLoading}
      />
      <ConfirmationModal
        open={isIssueModalOpen}
        title="Issue Sale Orders"
        body="This will issue all selected sale orders, are you sure?"
        onCancelClicked={closeIssueModalLocal}
        onConfirmClicked={handleIssueConfirmAndClose}
        confirmLabel={'Issue'}
        isLoading={isIssueProcessing}
      />
      <CustomFieldsModal
        open={isCustomFieldsModalOpen}
        module={ObjectType.SalesOrder}
        setVisible={setIsCustomFieldsModalOpen}
      />
      <ReportsModal
        isOpen={isReportModalOpen}
        reportId={ReportId.SalesOrder}
        params={{ objectId: activeSalesOrderId }}
        onClose={closeReportModal}
      />
      <BulkQuickFulfillModal
        estimatedTimePerOrderSeconds={estimatedTimePerOrderSeconds}
        open={isBulkQuickFulfillModalOpen}
        eligibleOrders={bulkQuickFulfillEligibleOrders}
        ineligibleOrders={bulkQuickFulfillIneligibleOrders}
        stage={bulkQuickFulfillStage}
        progressDetails={bulkQuickFulfillProgressDetails}
        onClose={closeBulkQuickFulfillModal}
        onConfirmAndProcess={confirmAndProcessBulkQuickFulfill}
        onStopProcessing={stopBulkQuickFulfillProcessing}
        isStopping={isStoppingBulkQuickFulfill}
      />

      <input
        type="file"
        ref={fileImportInputRef}
        style={{ display: 'none' }}
        onChange={onFileSelectedForImport}
        accept=".csv, .xls, .xlsx"
      />
    </ErrorBoundary>
  );
}
